import { Box, Container, Flex, Heading, Text, Link } from '@everlywell/ui-kit';
import Layout from 'components/Layout';
import ProductCarousel from 'components/ProductCarousel/ProductCarousel';
import { parseProductCarouselData } from 'components/ProductCarousel/ProductCarousel.parser';
import { SEO } from 'components/SEO';
import ShapeCards from 'components/ShapeCards';
import { parseShapeCards } from 'components/ShapeCards/ShapeCards.parser';
import { graphql, PageProps } from 'gatsby';
import { GatsbyImage, getImage, IGatsbyImageData } from 'gatsby-plugin-image';
import React, { useEffect, useMemo } from 'react';
import { track } from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';
import useAdvertisedPromos from 'utils/hooks/useAdvertisedPromos';

import blogTagIcon from '../images/blog-tag-icon.png';

interface Subcategory {
  title: string;
  id: string;
  slug: string;
}

interface QAPost {
  id: string;
  slug: string;
  title: string;
  metaTitle?: string;
  canonicalUrl?: string;
  publishDate: string;
  publishDateISO: string;
  faqSchema?: Array<{
    question: string;
    answer: string;
  }>;
  description?: {
    internal: {
      content: string;
    };
  };
  subcategories?: Subcategory[];
  heroImage?: {
    title?: string;
    gatsbyImageData: IGatsbyImageData;
    description?: string;
  };
  body: {
    body?: string;
    childMarkdownRemark: {
      html: string;
      excerpt: string;
    };
  };
}

interface QaArticleData {
  contentfulPost: QAPost;
  prevPost: QAPost | null;
  nextPost: QAPost | null;
  homepage: {
    sections: Array<Queries.ContentfulSection001>;
  } | null;
}

// Blog tag icon
const BlogTagIcon = () => (
  <img
    src={blogTagIcon}
    alt=""
    width="16"
    height="16"
    style={{ display: 'block' }}
  />
);

type QAArticleProps = PageProps<
  QaArticleData,
  { slug: string; prevSlug: string | null; nextSlug: string | null }
>;

const QAArticle = ({ data }: QAArticleProps): React.ReactElement => {
  const post = data.contentfulPost;
  const prev = data.prevPost;
  const next = data.nextPost;
  const basePath = 'answers';
  const heroImage = post.heroImage
    ? getImage(post.heroImage.gatsbyImageData)
    : null;

  // Get advertised promos for ProductCarousel
  const { advertisedPromos } = useAdvertisedPromos();

  // Extract homepage sections for Latest Offers and Top Picks
  const homepageSections = data.homepage?.sections || [];
  const latestOffersSection = homepageSections.find(
    (section) => section.componentName === 'ShapeCards',
  );
  const topPicksSection = homepageSections.find(
    (section) => section.componentName === 'ProductCarousel',
  );

  // Parse the Latest Offers section data for ShapeCards component
  const shapeCardsProps = useMemo(() => {
    if (!latestOffersSection) return null;
    try {
      return parseShapeCards(latestOffersSection);
    } catch {
      return null;
    }
  }, [latestOffersSection]);

  // Parse the Top Picks section data for ProductCarousel component
  const productCarouselProps = useMemo(() => {
    if (!topPicksSection) return null;
    try {
      return parseProductCarouselData(topPicksSection, advertisedPromos);
    } catch {
      return null;
    }
  }, [topPicksSection, advertisedPromos]);

  useEffect(() => {
    track({
      event: ANALYTICS.EVENTS.VIEWED_PAGE,
      data: {
        label: basePath === 'answers' ? 'Q&A Article' : 'Blog Article',
        article: post.title,
      },
    });
  }, [post.title, basePath]);

  return (
    <Layout>
      {/* Hero Section */}
      <Box
        backgroundColor="#DFF6FF"
        width="100%"
        minHeight={['auto', null, '460px']}
        overflow="hidden"
        position="relative"
      >
        <Flex
          maxWidth="1440px"
          marginX="auto"
          minHeight={['auto', null, '460px']}
          flexDirection={['column', null, 'row']}
          alignItems={['flex-start', null, 'center']}
        >
          {/* Left Content */}
          <Box
            flex="1"
            paddingX={[6, null, 12]}
            paddingY={[10, null, 0]}
            maxWidth={['100%', null, '720px']}
          >
            <Flex
              flexDirection="column"
              gap={[6, null, 8]}
              alignItems="flex-start"
            >
              {/* Blog Tag */}
              <Flex
                alignItems="center"
                gap="4px"
                backgroundColor="white"
                borderRadius="20px"
                height="28px"
                paddingX={3}
                paddingY={2}
              >
                <BlogTagIcon />
                <Text
                  fontSize="12px"
                  fontWeight="medium"
                  color="#545454"
                  lineHeight="1.5"
                >
                  Blog
                </Text>
              </Flex>

              {/* Title */}
              <Heading
                as="h1"
                fontSize={['32px', null, '44px']}
                fontWeight="normal"
                lineHeight="1.2"
                color="#0F0F0F"
                maxWidth="684px"
              >
                {post.title}
              </Heading>

              {/* Categories as links */}
              {post.subcategories && post.subcategories.length > 0 && (
                <Flex flexWrap="wrap" gap="4px" alignItems="center">
                  {post.subcategories.map((subcategory, index) => (
                    <React.Fragment key={subcategory.id}>
                      <Link
                        href={`/${basePath}#${subcategory.slug}`}
                        fontSize="16px"
                        color="#545454"
                        lineHeight="1.5"
                        textDecoration="underline"
                        _hover={{ color: '#28724F' }}
                      >
                        {subcategory.title}
                      </Link>
                      {post?.subcategories && index < post.subcategories.length - 1 && (
                        <Text fontSize="16px" color="#545454">
                          ,&nbsp;
                        </Text>
                      )}
                    </React.Fragment>
                  ))}
                </Flex>
              )}
            </Flex>
          </Box>

          {/* Right Hero Image */}
          {heroImage && (
            <Box
              width={['100%', null, '50%']}
              height={['300px', null, '420px']}
              position="relative"
              display={['block', null, 'flex']}
              alignItems="flex-end"
              justifyContent="center"
              paddingX={[6, null, 0]}
              paddingBottom={[6, null, 0]}
            >
              <Box
                width={['100%', null, '480px']}
                height={['280px', null, '420px']}
                borderRadius={['16px', null, '32px 32px 0 0']}
                overflow="hidden"
              >
                <GatsbyImage
                  image={heroImage}
                  alt={post.heroImage?.description || post.title}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </Box>
            </Box>
          )}
        </Flex>
      </Box>

      {/* Article Content */}
      <Container
        maxWidth="800px"
        marginX="auto"
        paddingTop={[10, null, 15]}
        paddingBottom={[10, null, 15]}
        paddingX={6}
      >
        <Box
          fontSize="lg"
          lineHeight="1.75"
          color="tints.darkGray"
          sx={{
            h2: {
              fontSize: '2xl',
              fontWeight: 'semibold',
              marginTop: 10,
              marginBottom: 4,
              color: 'tints.black',
            },
            h3: {
              fontSize: 'xl',
              fontWeight: 'semibold',
              marginTop: 8,
              marginBottom: 3,
              color: 'tints.black',
            },
            h4: {
              fontSize: 'lg',
              fontWeight: 'semibold',
              marginTop: 6,
              marginBottom: 2,
              color: 'tints.black',
            },
            p: {
              marginBottom: 6,
            },
            'ul, ol': {
              marginBottom: 6,
              paddingLeft: 6,
            },
            li: {
              marginBottom: 2,
            },
            a: {
              color: 'green.500',
              textDecoration: 'underline',
              _hover: {
                color: 'green.600',
              },
            },
            blockquote: {
              borderLeft: '4px solid',
              borderColor: 'green.500',
              paddingLeft: 4,
              marginY: 6,
              fontStyle: 'italic',
              color: 'tints.darkGray',
            },
            img: {
              maxWidth: '100%',
              height: 'auto',
              borderRadius: 'md',
              marginY: 6,
            },
            'b, strong': {
              fontWeight: 'semibold',
            },
          }}
          dangerouslySetInnerHTML={{
            __html: post.body.body || post.body.childMarkdownRemark.html,
          }}
        />

        <Flex
          justifyContent="space-between"
          marginTop={12}
          paddingTop={6}
          borderTop="1px solid"
          borderColor="tints.darkCream"
          gap={4}
        >
          {prev ? (
            <Link
              href={`/${basePath}/${prev.slug}/`}
              fontSize="sm"
              maxWidth="45%"
            >
              ← {prev.title}
            </Link>
          ) : (
            <Box />
          )}
          {next ? (
            <Link
              href={`/${basePath}/${next.slug}/`}
              fontSize="sm"
              maxWidth="45%"
              textAlign="right"
            >
              {next.title} →
            </Link>
          ) : (
            <Box />
          )}
        </Flex>
      </Container>

      {/* Latest Offers Section */}
      {shapeCardsProps && (
        <Box paddingY={[10, null, 20]}>
          <ShapeCards {...shapeCardsProps} />
        </Box>
      )}

      {/* Top Picks Section */}
      {productCarouselProps && (
        <Box paddingY={[10, null, 20]}>
          <ProductCarousel {...productCarouselProps} />
        </Box>
      )}
    </Layout>
  );
};

// GraphQL page query to fetch blog article data
export const query = graphql`
  query QAArticleQuery($slug: String!, $prevSlug: String, $nextSlug: String) {
    contentfulPost(slug: { eq: $slug }) {
      id
      slug
      title
      metaTitle
      canonicalUrl
      publishDate(formatString: "MMMM DD, YYYY")
      publishDateISO: publishDate(formatString: "YYYY-MM-DD")
      updatedAt
      faqSchema {
        question
        answer
      }
      description {
        internal {
          content
        }
      }
      subcategories {
        title
        id
        slug
      }
      heroImage {
        title
        gatsbyImageData(placeholder: BLURRED)
        description
      }
      body {
        body
        childMarkdownRemark {
          html
          excerpt(pruneLength: 320)
        }
      }
    }

    prevPost: contentfulPost(slug: { eq: $prevSlug }) {
      id
      slug
      title
      subcategories {
        slug
      }
    }

    nextPost: contentfulPost(slug: { eq: $nextSlug }) {
      id
      slug
      title
      subcategories {
        slug
      }
    }

    homepage: contentfulPage001(slug: { eq: "homepage" }) {
      sections {
        componentName
        eyebrow
        displayName
        metadata {
          title
          description {
            description
            childMarkdownRemark {
              html
            }
          }
          ctas {
            label
            url
          }
          flags {
            displayName
            displayText
            value
          }
        }
        subsections {
          ... on ContentfulSection001 {
            componentName
            eyebrow
            body {
              raw
            }
            images {
              desktopImageAltText
              desktopImage {
                gatsbyImageData(quality: 75, placeholder: BLURRED)
              }
              mobileImageAltText
              mobileImage {
                gatsbyImageData(quality: 75, placeholder: BLURRED)
              }
            }
            metadata {
              title
              description {
                description
              }
              ctas {
                label
                url
              }
              theme {
                backgroundColor
                textColor
              }
            }
          }
          ... on ContentfulCarousel001 {
            items {
              ... on ContentfulCarouselItem001 {
                content {
                  subsections {
                    ... on ContentfulProduct001 {
                      slug
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export default QAArticle;

// Gatsby Head API for server-side head management
export const Head = ({ data }: QAArticleProps) => {
  const post = data.contentfulPost;
  const basePath = 'answers';
  const description =
    post.description?.internal?.content ||
    post.body.childMarkdownRemark.excerpt;

  // Get hero image URL for og:image and Article schema
  const heroImageUrl = post.heroImage?.gatsbyImageData?.images?.fallback?.src;

  // Build Article structured data
  const siteUrl = 'https://www.everlywell.com';
  const articleUrl = `${siteUrl}/${basePath}/${post.slug}/`;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: description,
    image: heroImageUrl ? `https:${heroImageUrl}` : undefined,
    datePublished: post.publishDateISO,
    dateModified: post.publishDateISO,
    author: {
      '@type': 'Organization',
      name: 'Everlywell',
      url: siteUrl,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Everlywell',
      logo: {
        '@type': 'ImageObject',
        url: `${siteUrl}/icons/icon-512x512.png`,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': articleUrl,
    },
  };

  // Combine Article schema with FAQ schema if available
  const structuredData = post.faqSchema?.length
    ? [
        articleSchema,
        {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: post.faqSchema.map((faq) => ({
            '@type': 'Question',
            name: faq.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: faq.answer,
            },
          })),
        },
      ]
    : articleSchema;

  return (
    <SEO
      title={post.metaTitle || post.title}
      description={description}
      slug={`${basePath}/${post.slug}`}
      canonicalUrl={post.canonicalUrl}
      structuredDataOverride={structuredData as any}
    />
  );
};
